import { faBoltLightning, faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ColDef,
  ModuleRegistry,
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ColumnAutoSizeModule,
  ICellRendererParams,
  ValueGetterParams,
  GridApi,
  GridOptions
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import type { JSX } from 'react';
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePagination from '@/hooks/usePagination';
import RemitRepository from '@/repositories/RemitRepository';
import { Remit } from '@/types/billing';
import { DIRECTION } from '@/types/sort';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import Tooltip from '@/ui/components/Tooltip';
import { CandidLogo } from '@/ui/components/ViewInCandid/CandidLogo';
import { currencyInCentsFormatter } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import { titleize } from '@/utils/titleize';

// Register AG Grid modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ColumnAutoSizeModule
]);

export const listRemitsDefaults = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1',
  search: '',
  type: '',
  status: '',
  ingested_from: '',
  ingested_at__gte: '',
  ingested_at__lte: '',
  transmission_on__gte: '',
  transmission_on__lte: ''
};
const ListRemits = (): JSX.Element | null => {
  const { addMessage } = useContext(AppContext);
  const remitRepo = RemitRepository();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeParams = {
    direction: searchParams.get('direction') ?? listRemitsDefaults.direction,
    ordering: searchParams.get('ordering') ?? listRemitsDefaults.ordering,
    page: searchParams.get('page') ?? listRemitsDefaults.page,
    search: searchParams.get('search') ?? listRemitsDefaults.search,
    type: searchParams.get('type') ?? listRemitsDefaults.type,
    status: searchParams.get('status') ?? listRemitsDefaults.status,
    ingested_from: searchParams.get('ingested_from') ?? listRemitsDefaults.ingested_from,
    ingested_at__gte: searchParams.get('ingested_at__gte') ?? listRemitsDefaults.ingested_at__gte,
    ingested_at__lte: searchParams.get('ingested_at__lte') ?? listRemitsDefaults.ingested_at__lte,
    transmission_on__gte:
      searchParams.get('transmission_on__gte') ?? listRemitsDefaults.transmission_on__gte,
    transmission_on__lte:
      searchParams.get('transmission_on__lte') ?? listRemitsDefaults.transmission_on__lte
  };

  const gridOptions: GridOptions<Remit> = {
    autoSizeStrategy: {
      type: 'fitGridWidth' as const
    }
  };

  const gridApiRef = useRef<GridApi<Remit> | null>(null);

  const syncFiltersToSearchParams = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const model = api.getFilterModel() as Record<string, unknown>;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      [
        'type',
        'status',
        'ingested_from',
        'ingested_at',
        'ingested_at__gte',
        'ingested_at__lte',
        'transmission_on',
        'transmission_on__gte',
        'transmission_on__lte'
      ].forEach((p) => next.delete(p));

      const ensureString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

      Object.entries(model).forEach(([field, value]) => {
        if (!value || typeof value !== 'object') return;
        const v = value as Record<
          string,
          { filter?: unknown; type?: string; dateFrom?: string; dateTo?: string }
        >;
        switch (field) {
          case 'type':
          case 'status':
            if (ensureString(v.filter)) next.set(field, v.filter);
            break;
          case 'ingested_from':
            if (ensureString(v.filter)) next.set(field, v.filter);
            break;
          case 'ingested_at': {
            const type = v.type as string | undefined;
            const dateFrom = v.dateFrom as string | undefined;
            const dateTo = v.dateTo as string | undefined;
            if (!type) break;
            if (type === 'equals' && dateFrom) {
              next.set('ingested_at', dateFrom);
            } else if (type === 'greaterThan' && dateFrom) {
              next.set('ingested_at__gte', dateFrom);
            } else if (type === 'lessThan' && dateFrom) {
              next.set('ingested_at__lte', dateFrom);
            } else if (type === 'inRange') {
              if (dateFrom) next.set('ingested_at__gte', dateFrom);
              if (dateTo) next.set('ingested_at__lte', dateTo);
            }
            break;
          }
          case 'transmission_on': {
            const type = v.type as string | undefined;
            const dateFrom = v.dateFrom as string | undefined;
            const dateTo = v.dateTo as string | undefined;
            if (!type) break;
            if (type === 'equals' && dateFrom) {
              next.set('transmission_on', dateFrom);
            } else if (type === 'greaterThan' && dateFrom) {
              next.set('transmission_on__gte', dateFrom);
            } else if (type === 'lessThan' && dateFrom) {
              next.set('transmission_on__lte', dateFrom);
            } else if (type === 'inRange') {
              if (dateFrom) next.set('transmission_on__gte', dateFrom);
              if (dateTo) next.set('transmission_on__lte', dateTo);
            }
            break;
          }
          default:
            break;
        }
      });

      next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  const handleFilterChanged = useCallback(() => {
    syncFiltersToSearchParams();
  }, [syncFiltersToSearchParams]);

  const handleGridReady = useCallback((params: { api: GridApi<Remit> }) => {
    gridApiRef.current = params.api;
  }, []);

  const columnDefs: ColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellRenderer: (params: ICellRendererParams<Remit>) => (
        <Link to={`/billing/remit/${params.value}/edit`}>{params.value}</Link>
      )
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: (params: ICellRendererParams<Remit>) => {
        if (!params.data) return null;
        return (
          <Tooltip
            tooltipContent={
              params.data.type == 'paper' ? 'Paper' : `Electronic (${params.data.ingested_from})`
            }>
            {params.data.type == 'paper' ? (
              <FontAwesomeIcon icon={faFile} className="pl-2" />
            ) : params.data.ingested_from == 'candid' ? (
              <CandidLogo size={16} />
            ) : (
              <FontAwesomeIcon icon={faBoltLightning} className="pl-2" />
            )}
          </Tooltip>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      filter: 'agTextColumnFilter',
      valueFormatter: (params) => titleize(String(params.value))
    },
    {
      field: 'ingested_at',
      headerName: 'Ingested',
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'ingested_from',
      hide: true,
      sortable: true,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'transmission_on',
      headerName: 'Transmission On',
      sortable: true,
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : '')
    },
    {
      field: 'financial_info',
      headerName: 'Checks',
      sortable: false,
      filter: false,
      valueGetter: (params) => params.data?.financial_info?.length || 0
    },
    {
      field: 'financial_info',
      headerName: 'Total *',
      sortable: false,
      filter: false,
      valueGetter: (params: ValueGetterParams<Remit>) => {
        const totals = (params.data?.financial_info || [])
          .map((fi) => fi?.check_amount_in_cents || 0)
          .filter((amt): amt is number => typeof amt === 'number');
        const total = totals.reduce((acc, curr) => acc + curr, 0);
        return currencyInCentsFormatter(total);
      }
    },
    {
      field: 'financial_info',
      headerName: 'Paid At',
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Remit>) => {
        const paidAts = (params.data?.financial_info || [])
          .map((fi) => fi?.paid_at)
          .filter((paidAt): paidAt is string => Boolean(paidAt))
          .filter((paidAt, index, self) => self.indexOf(paidAt) === index)
          .map((paidAt) => (
            <span key={paidAt} className="mr-2">
              {new Date(paidAt).toDateString()}
            </span>
          ));
        return <div>{paidAts}</div>;
      }
    },
    {
      field: 'id',
      headerName: 'Actions',
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<Remit>) => (
        <Link
          to={`/billing/remit/${params.value}/edit`}
          className="text-indigo-600 hover:text-indigo-900">
          Edit
        </Link>
      )
    }
  ];

  const getRemitQuery = useAPI({
    queryKey: ['getRemits', activeParams],
    queryFn: async () => {
      const {
        direction,
        ordering,
        page,
        search,
        type,
        status,
        ingested_from,
        ingested_at__gte,
        ingested_at__lte,
        transmission_on__gte,
        transmission_on__lte
      } = activeParams;
      return await remitRepo.listRemits({
        ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
        page,
        ...(search && { search }),
        ...(type && { type }),
        ...(status && { status }),
        ...(ingested_from && { ingested_from }),
        ...(ingested_at__gte && { ingested_at__gte }),
        ...(ingested_at__lte && { ingested_at__lte }),
        ...(transmission_on__gte && { transmission_on__gte }),
        ...(transmission_on__lte && { transmission_on__lte })
      });
    }
  });
  const { navigateToNewRemit, navgiateToBillingDashboard } = useNavigateToPage();

  const { data, isPending, isSuccess, isError, error } = getRemitQuery;
  const remits = data?.data?.results || [];

  const [totalPages, setTotalPages] = useState<number>(0);
  useEffect(() => {
    if (isSuccess && data.data?.total_pages) {
      setTotalPages(data.data.total_pages);
    } else if (isError) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  }, [addMessage, data?.data?.total_pages, error, isError, isSuccess]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    if (currentPage !== +listRemitsDefaults.page)
      setSearchParams((currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      });
    else
      setSearchParams((currentParams) => {
        currentParams.delete('page');
        return currentParams;
      });
    // setSearchParams dependency was causing infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <Container type="page">
      <div className="md:flex md:items-center md:justify-between">
        <PageHeader headingText="Remits"></PageHeader>
        <div className="mt-4 flex md:ml-4 md:mt-0 gap-3">
          <Button size="sm" onClick={() => navigateToNewRemit()}>
            New Remit
          </Button>
          <Button size="sm" onClick={() => navgiateToBillingDashboard()}>
            Back
          </Button>
        </div>
      </div>
      {isPending && <Spinner></Spinner>}
      <div className="mb-4 flex space-x-2">
        <Button size="xs" onClick={() => setSearchParams({ type: 'paper' })}>
          Paper
        </Button>
        <Button size="xs" onClick={() => setSearchParams({ ingested_from: 'candid' })}>
          Candid
        </Button>
        <Button size="xs" onClick={() => setSearchParams({ ingested_from: 'office_ally' })}>
          Office Ally
        </Button>
        <Button
          size="xs"
          onClick={() => {
            const params = { ...activeParams };
            params.type = '';
            params.ingested_from = '';
            setSearchParams(params);
          }}>
          All
        </Button>
      </div>
      <Card className="overflow-x-auto p-0 mt-2" paddingClass="p-0">
        {/* A set of quick filters */}
        {/* Buttons for type (paper, candid, office_ally) */}

        <div className="ag-theme-alpine">
          <AgGridReact<Remit>
            rowData={remits}
            columnDefs={columnDefs}
            pagination={false}
            domLayout="autoHeight"
            gridOptions={gridOptions}
            onFilterChanged={handleFilterChanged}
            onGridReady={handleGridReady}
          />
        </div>
      </Card>
      <div className="mt-4">
        <Paginator
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}
        />
      </div>
    </Container>
  );
};

export default ListRemits;
